import { useState, useRef, useEffect } from 'react'
import { useNavigate, useLocation, Outlet } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useIncidentAlert } from '../hooks/useIncidentAlert'
import {
  Box,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  AppBar,
  Toolbar,
  Typography,
  Avatar,
  IconButton,
  Tooltip,
  Divider,
  useTheme,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemAvatar,
  Stack,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Snackbar,
  Alert,
  Button,
} from '@mui/material'
import DashboardIcon from '@mui/icons-material/Dashboard'
import SettingsIcon from '@mui/icons-material/Settings'
import LogoutIcon from '@mui/icons-material/Logout'
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth'
import PersonIcon from '@mui/icons-material/Person'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TuneIcon from '@mui/icons-material/Tune'
import TranslateIcon from '@mui/icons-material/Translate'
import DarkModeIcon from '@mui/icons-material/DarkMode'
import LightModeIcon from '@mui/icons-material/LightMode'
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness'
import CheckIcon from '@mui/icons-material/Check'
import GetAppIcon from '@mui/icons-material/GetApp'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { useAccount } from '../hooks/useAccount'
import { useSwipeNavigation } from '../hooks/useSwipeNavigation'
import { useConfirmIncident, DASHBOARD_KEY } from '../hooks/useDashboard'
import { PREFERENCES_KEY } from '../hooks/usePreferences'
import { USERS_KEY } from '../hooks/useAdmin'
import { getDashboardStatus } from '../api/dashboard'
import { getPreferences } from '../api/preferences'
import { listUsers } from '../api/admin'
import { logout } from '../api/auth'
import { useThemeMode, type ThemeMode } from '../theme/ThemeContext'
import { usePwaInstall } from '../hooks/usePwaInstall'

const DRAWER_WIDTH = 240
const DRAWER_COLLAPSED_WIDTH = 64
// Must match the MUI width transition so labels only appear once the drawer is open
const TRANSITION_MS = 300


type NavKey = 'dashboard' | 'sessions' | 'notifications' | 'myPage' | 'admin'

const NAV_ITEMS: { key: NavKey; path: string; icon: React.ReactNode; adminOnly: boolean }[] = [
  { key: 'dashboard',     path: '/dashboard',   icon: <DashboardIcon />,          adminOnly: false },
  { key: 'sessions',      path: '/sessions',    icon: <CalendarMonthIcon />,      adminOnly: false },
  { key: 'notifications', path: '/preferences', icon: <SettingsIcon />,           adminOnly: false },
  { key: 'myPage',        path: '/account',     icon: <PersonIcon />,             adminOnly: false },
  { key: 'admin',         path: '/admin',       icon: <AdminPanelSettingsIcon />, adminOnly: true  },
]

const LANGUAGES = [
  { code: 'en', label: 'English', flag: '\u{1F1FA}\u{1F1F8}' },
  { code: 'ru', label: '\u0420\u0443\u0441\u0441\u043a\u0438\u0439', flag: '\u{1F1F7}\u{1F1FA}' },
]

const THEME_OPTIONS: { mode: ThemeMode; icon: React.ReactNode; labelKey: string }[] = [
  { mode: 'dark',   icon: <DarkModeIcon fontSize="small" />,            labelKey: 'settings.themeDark' },
  { mode: 'light',  icon: <LightModeIcon fontSize="small" />,           labelKey: 'settings.themeLight' },
  { mode: 'system', icon: <SettingsBrightnessIcon fontSize="small" />,   labelKey: 'settings.themeSystem' },
]

export default function Layout() {
  const { data: user } = useAccount()
  const navigate = useNavigate()
  const location = useLocation()
  const theme = useTheme()
  const queryClient = useQueryClient()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))
  const { t, i18n } = useTranslation()
  const { mode: themeMode, setMode: setThemeMode } = useThemeMode()
  const { canInstall, install } = usePwaInstall()
  const [loggingOut, setLoggingOut] = useState(false)
  const { popupIncident, dismissPopup } = useIncidentAlert()
  const { mutate: confirmIncident, isPending: isConfirming } = useConfirmIncident()

  // ── Optimistic-lock conflict snackbar ────────────────────────────────────
  const [conflictOpen, setConflictOpen] = useState(false)
  useEffect(() => {
    const handler = () => setConflictOpen(true)
    window.addEventListener('api:conflict', handler)
    return () => window.removeEventListener('api:conflict', handler)
  }, [])

  // ── Settings menu ────────────────────────────────────────────────────────
  const [settingsAnchor, setSettingsAnchor] = useState<null | HTMLElement>(null)

  // ── Desktop sidebar state — persisted across page loads ─────────────────
  const [collapsed, setCollapsedRaw] = useState(
    () => localStorage.getItem('sidebar-collapsed') === 'true'
  )
  const [textVisible, setTextVisible] = useState(
    () => localStorage.getItem('sidebar-collapsed') !== 'true'
  )
  const expandTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  const setCollapsed = (value: boolean) => {
    localStorage.setItem('sidebar-collapsed', String(value))
    setCollapsedRaw(value)
    clearTimeout(expandTimerRef.current)
    if (value) {
      setTextVisible(false)
    } else {
      expandTimerRef.current = setTimeout(() => setTextVisible(true), TRANSITION_MS * 0.6)
    }
  }

  // ── Swipe navigation (mobile only) ─────────────────────────────────────────
  const swipeContainerRef = useRef<HTMLDivElement>(null)
  const [swipeDirForPath, setSwipeDirForPath] = useState<{ path: string; dir: 'left' | 'right' } | null>(null)
  const swipeDir = swipeDirForPath?.path === location.pathname ? swipeDirForPath.dir : null

  const handleLogout = async () => {
    setLoggingOut(true)
    await logout()
  }

  const handleNav = (path: string) => {
    navigate(path)
  }

  const isDark = theme.palette.mode === 'dark'
  const borderColor = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'

  const isAdmin = user?.role === 'ADMIN'
  const visibleItems = NAV_ITEMS.filter((item) => !item.adminOnly || isAdmin)

  // Pre-warm the Lambda before the user lifts their finger (~150-300ms head start)
  const prefetchForPath = (path: string) => {
    if (path === '/dashboard')   queryClient.prefetchQuery({ queryKey: DASHBOARD_KEY,   queryFn: getDashboardStatus })
    if (path === '/preferences') queryClient.prefetchQuery({ queryKey: PREFERENCES_KEY, queryFn: getPreferences })
    if (path === '/admin')       queryClient.prefetchQuery({ queryKey: USERS_KEY,        queryFn: listUsers })
  }

  const navCount   = visibleItems.length
  const activeNavIndex = visibleItems.findIndex((item) => item.path === location.pathname)
  const baseNavIdx = activeNavIndex >= 0 ? activeNavIndex : 0

  const swipePages = visibleItems.map((i) => i.path)
  useSwipeNavigation(
    isDesktop ? { current: null } : swipeContainerRef,
    swipePages,
    (dir, toPath) => setSwipeDirForPath({ path: toPath, dir }),
    isDesktop ? undefined : swipeContainerRef,
  )

  const pageTitle = (() => {
    const found = visibleItems.find((i) => i.path === location.pathname)
    return found ? t(`nav.${found.key}`) : t('brand.name')
  })()

  // On desktop, drawer may be collapsed; on mobile there is no drawer
  const showText = textVisible
  const drawerWidth = collapsed ? DRAWER_COLLAPSED_WIDTH : DRAWER_WIDTH
  const widthTransition = theme.transitions.create('width', { duration: TRANSITION_MS })

  // Shared opacity + slide transition style for text blocks
  const textFade = (visible: boolean) => ({
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateX(0)' : 'translateX(-8px)',
    transition: visible
      ? 'opacity 200ms ease, transform 200ms ease'
      : 'opacity 60ms ease, transform 60ms ease',
    pointerEvents: visible ? ('auto' as const) : ('none' as const),
  })

  // ── Drawer content (shared between permanent and temporary) ─────────────
  const drawerContent = (
    <>
      {/* ── Logo / brand ── */}
      <Box
        sx={{
          height: 56,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
        }}
      >
        <Box
          onClick={() => handleNav('/dashboard')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            flex: showText ? 1 : 0,
            pl: showText ? 1.5 : 0,
            ...textFade(showText),
          }}
        >
          <NotificationsActiveIcon sx={{ color: 'primary.main', fontSize: 26, flexShrink: 0 }} />
          <Box>
            <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
              {t('brand.name')}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              {t('brand.tagline')}
            </Typography>
          </Box>
        </Box>

        {/* Collapse toggle — desktop only */}
        {isDesktop && (
          <Tooltip
            title={collapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
            placement="right"
          >
            <IconButton
              size="small"
              onClick={() => setCollapsed(!collapsed)}
              sx={{
                color: 'text.secondary',
                flexShrink: 0,
                mr: showText ? 1 : 0,
              }}
            >
              {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <Divider sx={{ borderColor }} />

      {/* ── Navigation ── */}
      <List sx={{ pt: 1, flex: 1 }}>
        {visibleItems.map((item) => {
          const active = location.pathname === item.path
          const label = t(`nav.${item.key}`)
          return (
            <Tooltip
              key={item.path}
              title={!showText ? label : ''}
              placement="right"
            >
              <ListItemButton
                onClick={() => handleNav(item.path)}
                selected={active}
                sx={{
                  mx: 1,
                  borderRadius: 2,
                  mb: 0.5,
                  px: 1.5,
                  justifyContent: showText ? 'flex-start' : 'center',
                  '&.Mui-selected': {
                    background: 'rgba(66,133,244,0.15)',
                    '& .MuiListItemIcon-root': { color: 'primary.main' },
                    '& .MuiListItemText-primary': { color: 'primary.main', fontWeight: 700 },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: showText ? 40 : 0,
                    color: 'text.secondary',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {item.icon}
                </ListItemIcon>

                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: showText ? 1 : 0,
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    ...textFade(showText),
                  }}
                >
                  <ListItemText primary={label} sx={{ m: 0 }} />
                </Box>
              </ListItemButton>
            </Tooltip>
          )
        })}
      </List>

      <Divider sx={{ borderColor }} />

      {/* ── User section ── */}
      {user && (
        <Box
          sx={{
            px: 1.5,
            py: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: showText ? 1.5 : 0,
            justifyContent: showText ? 'flex-start' : 'center',
            overflow: 'hidden',
          }}
        >
          <Tooltip
            title={!showText ? user.name ?? user.email : t('nav.viewAccount')}
            placement="right"
          >
            <Avatar
              src={user.avatarUrl}
              alt={user.name ?? user.email}
              sx={{
                width: 34,
                height: 34,
                fontSize: '0.85rem',
                flexShrink: 0,
                cursor: 'pointer',
              }}
              slotProps={{ img: { referrerPolicy: 'no-referrer' } }}
              onClick={() => handleNav('/account')}
            >
              {(user.name ?? user.email ?? '?').charAt(0).toUpperCase()}
            </Avatar>
          </Tooltip>

          <Box
            sx={{
              flex: showText ? 1 : 0,
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              overflow: 'hidden',
              ...textFade(showText),
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" fontWeight={600} noWrap>
                {user.name ?? user.email}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap display="block">
                {user.email}
              </Typography>
            </Box>
            <Tooltip title={t('nav.logout')}>
              <IconButton
                size="small"
                onClick={handleLogout}
                disabled={loggingOut}
                sx={{ color: 'text.secondary', flexShrink: 0 }}
              >
                <LogoutIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </>
  )

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* ── Sidebar — desktop only (permanent) ── */}
      {isDesktop && (
        <Drawer
          variant="permanent"
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            transition: widthTransition,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
              background: theme.palette.background.paper,
              borderRight: `1px solid ${borderColor}`,
              overflowX: 'hidden',
              transition: widthTransition,
            },
          }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* ── Main area ── */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <AppBar
          position="static"
          elevation={0}
          sx={{
            background: 'transparent',
            borderBottom: `1px solid ${borderColor}`,
          }}
        >
          <Toolbar sx={{ justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h6" fontWeight={700} color="text.primary">
                {pageTitle}
              </Typography>
            </Box>

            <Stack direction="row" alignItems="center" spacing={0.5}>
              {/* Install PWA — appears automatically when browser allows it */}
              {canInstall && (
                <Tooltip title={t('nav.installApp')}>
                  <IconButton onClick={install} sx={{ color: 'primary.main' }}>
                    <GetAppIcon />
                  </IconButton>
                </Tooltip>
              )}

              {/* ── Settings button ── */}
              <Tooltip title={t('settings.theme')}>
                <IconButton
                  onClick={(e) => setSettingsAnchor(e.currentTarget)}
                  sx={{ color: 'text.secondary' }}
                >
                  <TuneIcon />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* ── Settings dropdown ── */}
            <Menu
              anchorEl={settingsAnchor}
              open={Boolean(settingsAnchor)}
              onClose={() => setSettingsAnchor(null)}
              slotProps={{
                paper: {
                  sx: {
                    minWidth: 200,
                    bgcolor: 'background.paper',
                    backgroundImage: 'none',
                    border: `1px solid ${borderColor}`,
                    borderRadius: 2,
                  },
                },
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {/* Language section */}
              <MenuItem disabled sx={{ opacity: '1 !important', pt: 1, pb: 0.5 }}>
                <ListItemAvatar sx={{ minWidth: 32 }}>
                  <TranslateIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </ListItemAvatar>
                <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                  {t('settings.language')}
                </Typography>
              </MenuItem>
              {LANGUAGES.map((lang) => (
                <MenuItem
                  key={lang.code}
                  selected={i18n.language.startsWith(lang.code)}
                  onClick={() => { i18n.changeLanguage(lang.code); setSettingsAnchor(null) }}
                  sx={{ pl: 3 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
                    <Typography fontSize={16}>{lang.flag}</Typography>
                    <Typography variant="body2">{lang.label}</Typography>
                  </Stack>
                  {i18n.language.startsWith(lang.code) && (
                    <CheckIcon fontSize="small" sx={{ color: 'primary.main', ml: 1 }} />
                  )}
                </MenuItem>
              ))}

              <Divider sx={{ my: 0.5, borderColor }} />

              {/* Theme section */}
              <MenuItem disabled sx={{ opacity: '1 !important', pt: 1, pb: 0.5 }}>
                <ListItemAvatar sx={{ minWidth: 32 }}>
                  <SettingsBrightnessIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                </ListItemAvatar>
                <Typography variant="caption" fontWeight={600} color="text.secondary" textTransform="uppercase" letterSpacing={0.5}>
                  {t('settings.theme')}
                </Typography>
              </MenuItem>
              {THEME_OPTIONS.map((opt) => (
                <MenuItem
                  key={opt.mode}
                  selected={themeMode === opt.mode}
                  onClick={() => { setThemeMode(opt.mode); setSettingsAnchor(null) }}
                  sx={{ pl: 3 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5} flex={1}>
                    <Box sx={{ color: 'text.secondary', display: 'flex' }}>{opt.icon}</Box>
                    <Typography variant="body2">{t(opt.labelKey)}</Typography>
                  </Stack>
                  {themeMode === opt.mode && (
                    <CheckIcon fontSize="small" sx={{ color: 'primary.main', ml: 1 }} />
                  )}
                </MenuItem>
              ))}
            </Menu>
          </Toolbar>
        </AppBar>

        {/* overflow:hidden clips the content while it's being dragged sideways */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          <Box
            ref={swipeContainerRef}
            sx={{ flex: 1, overflow: 'auto' }}
          >
          <Box
            key={location.pathname}
            sx={{
              p: { xs: 2, sm: 3 },
              pb: { xs: 11, sm: 3 },
              // Swipe gesture → slide in from the direction of travel
              // Tap navigation → simple fade (no slide)
              animation: swipeDir
                ? `${swipeDir === 'left' ? 'pageFromRight' : 'pageFromLeft'} 0.25s cubic-bezier(0.25,0.46,0.45,0.94) both`
                : 'pageFadeIn 0.18s ease both',
              '@keyframes pageFromRight': {
                from: { opacity: 0.4, transform: 'translateX(40px)' },
                to:   { opacity: 1,   transform: 'translateX(0)' },
              },
              '@keyframes pageFromLeft': {
                from: { opacity: 0.4, transform: 'translateX(-40px)' },
                to:   { opacity: 1,   transform: 'translateX(0)' },
              },
              '@keyframes pageFadeIn': {
                from: { opacity: 0 },
                to:   { opacity: 1 },
              },
            }}
          >
            <Outlet />
          </Box>
          </Box>  {/* /swipe-translate box */}
        </Box>  {/* /clip box */}
      </Box>

      {/* ── Active incident popup — all pages except Dashboard (has its own card) ── */}
      <Snackbar
        open={!!popupIncident && location.pathname !== '/dashboard'}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{ top: { xs: 70, sm: 80 } }}
      >
        <Alert
          severity="error"
          icon={<WarningAmberIcon />}
          onClose={dismissPopup}
          sx={{ width: '100%', alignItems: 'center' }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                size="small"
                color="inherit"
                variant="outlined"
                disabled={isConfirming}
                onClick={() => {
                  if (popupIncident) {
                    confirmIncident(popupIncident.incidentId, { onSuccess: dismissPopup })
                  }
                }}
              >
                {t('dashboard.confirmIncident')}
              </Button>
              <IconButton size="small" color="inherit" onClick={dismissPopup}>
                ✕
              </IconButton>
            </Stack>
          }
        >
          <Typography variant="subtitle2">{t('dashboard.activeIncidentTitle')}</Typography>
          <Typography variant="caption" display="block" color="inherit">
            {popupIncident?.startedAt
              ? t('dashboard.activeIncidentStarted', {
                  time: new Date(popupIncident.startedAt).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  }),
                })
              : ''}
          </Typography>
        </Alert>
      </Snackbar>

      {/* ── Optimistic-lock conflict snackbar ── */}
      <Snackbar
        open={conflictOpen}
        autoHideDuration={6000}
        onClose={() => setConflictOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ bottom: { xs: 72, sm: 24 } }}
      >
        <Alert
          severity="error"
          variant="filled"
          onClose={() => setConflictOpen(false)}
          sx={{ width: '100%' }}
        >
          {t('common.conflictRetry')}
        </Alert>
      </Snackbar>

      {/* ── Bottom navigation — mobile only ── */}
      {!isDesktop && (
        <Paper
          elevation={12}
          sx={{
            position: 'fixed',
            bottom: 12,
            left: 12,
            right: 12,
            zIndex: theme.zIndex.appBar,
            borderRadius: 4,
            overflow: 'hidden',
            background: theme.palette.background.paper,
            border: `1px solid ${borderColor}`,
          }}
        >
          {/* Pill indicator — teleports to new tab and fades in; no sliding */}
          <Box sx={{ position: 'relative' }}>
            {activeNavIndex >= 0 && (
              <Box
                key={baseNavIdx}
                sx={{
                  position: 'absolute',
                  top: '50%',
                  width: `calc(${100 / navCount}% - 16px)`,
                  height: 36,
                  borderRadius: '12px',
                  background: isDark ? 'rgba(66,133,244,0.18)' : 'rgba(66,133,244,0.12)',
                  left: `calc(${baseNavIdx * (100 / navCount)}% + 8px)`,
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  zIndex: 0,
                  animation: 'navIndicatorFadeIn 0.22s ease both',
                  '@keyframes navIndicatorFadeIn': {
                    from: { opacity: 0 },
                    to:   { opacity: 1 },
                  },
                }}
              />
            )}
            <BottomNavigation
              value={activeNavIndex === -1 ? false : activeNavIndex}
              onChange={(_, newValue: number) => handleNav(visibleItems[newValue].path)}
              showLabels={false}
              sx={{
                height: 58,
                background: 'transparent',
                position: 'relative',
                zIndex: 1,
                '& .MuiBottomNavigationAction-root': {
                  color: 'text.disabled',
                  minWidth: 0,
                  padding: '6px 0',
                  transition: 'color 0.25s ease',
                  '& .MuiBottomNavigationAction-label': { display: 'none' },
                  '& .MuiSvgIcon-root': { fontSize: '1.5rem' },
                },
                '& .MuiBottomNavigationAction-root.Mui-selected': {
                  color: 'primary.main',
                },
              }}
            >
              {visibleItems.map((item) => (
                <Tooltip key={item.path} title={t(`nav.${item.key}`)} placement="top">
                  <BottomNavigationAction
                    icon={item.icon}
                    onTouchStart={() => prefetchForPath(item.path)}
                  />
                </Tooltip>
              ))}
            </BottomNavigation>
          </Box>
        </Paper>
      )}
    </Box>
  )
}
